import { useEffect, useState, useCallback, useMemo } from "react";
import {
  initOrdersRealtime,
  isOrdersRealtimeActive,
} from "@/pages/orders-new/orders-realtime";

export interface OrderUpdate {
  id: string;
  status?: string;
  dispatch_date?: string;
  delivery_date?: string;
  driver_id?: number;
  location_lat?: number;
  location_lng?: number;
  // Add other fields that might be updated in real-time
}

export interface UseOrderUpdatesOptions {
  orderIds: string[];
  isEnabled: boolean;
  isTestMode?: boolean;
}

export const useOrderUpdates = ({
  orderIds,
  isEnabled,
  isTestMode = false,
}: UseOrderUpdatesOptions) => {
  const [updates, setUpdates] = useState<Record<string, OrderUpdate>>({});
  const [connectionStatus, setConnectionStatus] = useState<
    "connecting" | "connected" | "disconnected"
  >("disconnected");

  // Memoize the orderIds array to prevent unnecessary subscription updates
  const memoizedOrderIds = useMemo(() => {
    return orderIds.sort().join(",");
  }, [orderIds]);

  // Apply an update to a specific order
  const applyUpdate = useCallback(
    (orderId: string, update: Partial<OrderUpdate>) => {
      setUpdates((prev) => ({
        ...prev,
        [orderId]: {
          ...prev[orderId],
          ...update,
          id: orderId,
        },
      }));
    },
    [],
  );

  // Get the current state of an order (original + updates)
  const getOrderWithUpdates = useCallback(
    (originalOrder: any) => {
      const update = updates[originalOrder.id?.toString()];
      return update ? { ...originalOrder, ...update } : originalOrder;
    },
    [updates],
  );

  // Clear updates for specific orders
  const clearUpdates = useCallback((orderIdsToClear?: string[]) => {
    if (orderIdsToClear) {
      setUpdates((prev) => {
        const newUpdates = { ...prev };
        orderIdsToClear.forEach((id) => delete newUpdates[id]);
        return newUpdates;
      });
    } else {
      setUpdates({});
    }
  }, []);

  // Real-time subscription effect
  useEffect(() => {
    if (!isEnabled || isTestMode || !memoizedOrderIds) {
      setConnectionStatus("disconnected");
      return;
    }

    const orderIdsArray = memoizedOrderIds.split(",").filter(Boolean);
    if (orderIdsArray.length === 0) return;

    setConnectionStatus("connecting");

    // Ensure Socket.IO realtime is initialized
    initOrdersRealtime();
    setConnectionStatus(isOrdersRealtimeActive() ? "connected" : "connecting");

    // Event handlers
    const handleOpen = () => setConnectionStatus("connected");
    const handleClose = () => setConnectionStatus("disconnected");
    const handleUpdate = (ev: Event) => {
      const custom = ev as CustomEvent<{
        type: string;
        orderId?: number;
        order?: any;
      }>;
      const evt = custom.detail;
      if (!evt || !evt.orderId) return;

      const idStr = String(evt.orderId);
      if (!orderIdsArray.includes(idStr)) return;

      // For INSERT/UPDATE we merge the new order fields; ignore DELETE here
      if (evt.type === "INSERT" || evt.type === "UPDATE") {
        if (evt.order) {
          applyUpdate(idStr, evt.order);
        } else {
          // If no payload, at least mark an update event to trigger UI recompute
          applyUpdate(idStr, {});
        }
      }
    };

    window.addEventListener(
      "orders-realtime:open",
      handleOpen as EventListener,
    );
    window.addEventListener(
      "orders-realtime:close",
      handleClose as EventListener,
    );
    window.addEventListener(
      "orders-realtime:update",
      handleUpdate as EventListener,
    );

    // Cleanup listeners
    return () => {
      window.removeEventListener(
        "orders-realtime:open",
        handleOpen as EventListener,
      );
      window.removeEventListener(
        "orders-realtime:close",
        handleClose as EventListener,
      );
      window.removeEventListener(
        "orders-realtime:update",
        handleUpdate as EventListener,
      );
      setConnectionStatus("disconnected");
    };
  }, [memoizedOrderIds, isEnabled, isTestMode, applyUpdate]);

  return {
    updates,
    connectionStatus,
    applyUpdate,
    getOrderWithUpdates,
    clearUpdates,
    isConnected: connectionStatus === "connected",
  };
};
